import { getSavedPackingListRowIndexes } from "@RequestFormV2/utils/stripUnselectedPackingListRows";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

// Legacy errors carry the row index in the title, e.g. { title: "weight_piece_2", message }.
const LEGACY_TITLE_INDEX = /^(.+)_(\d+)$/;

/**
 * Empty rows are dropped before save (`stripUnselectedPackingListRows`), so API packing-list
 * error indexes follow the saved rows, not the form. Moves them back onto the form rows.
 * Commodity errors are dropped: the form keeps commodities on a storage row, so their
 * indexes cannot be matched and they stay in the toast.
 */
export function mapPackingListErrorsToFormRows(data: Record<string, unknown>, formRows: unknown): Record<string, unknown> {
  const packingListErrors = data.packing_list;
  if (!Array.isArray(packingListErrors) || !Array.isArray(formRows)) return data;

  const formIndexes = getSavedPackingListRowIndexes(formRows);

  const isLegacy = packingListErrors.some((item) => isPlainObject(item) && typeof item.title === "string");
  if (isLegacy) {
    const remapped = packingListErrors.flatMap((item) => {
      if (!isPlainObject(item) || typeof item.title !== "string") return [item];
      const match = LEGACY_TITLE_INDEX.exec(item.title);
      const formIndex = match ? formIndexes[Number(match[2])] : undefined;
      return match && formIndex !== undefined ? [{ ...item, title: `${match[1]}_${formIndex}` }] : [];
    });
    return { ...data, packing_list: remapped };
  }

  const remapped: Record<string, unknown>[] = Array.from({ length: formRows.length }, () => ({}));
  packingListErrors.forEach((rowErrors, savedIndex) => {
    const formIndex = formIndexes[savedIndex];
    if (formIndex === undefined || !isPlainObject(rowErrors)) return;
    const { good_customs_information: _commodityErrors, ...fieldErrors } = rowErrors;
    remapped[formIndex] = fieldErrors;
  });
  return { ...data, packing_list: remapped };
}
